import { useState, useMemo } from "react";
import { Ruler, Shirt, User, Camera, Loader2, Sparkles, Upload, X, Check } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { MeasurementWarning, validateMeasurement } from "@/components/MeasurementWarning";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Adjustment options for dropdowns (-6 to +6)
const ADJUSTMENT_OPTIONS = [
  { value: -6, label: "-6" }, { value: -5.75, label: "-5¾" }, { value: -5.5, label: "-5½" }, { value: -5.25, label: "-5¼" },
  { value: -5, label: "-5" }, { value: -4.75, label: "-4¾" }, { value: -4.5, label: "-4½" }, { value: -4.25, label: "-4¼" },
  { value: -4, label: "-4" }, { value: -3.75, label: "-3¾" }, { value: -3.5, label: "-3½" }, { value: -3.25, label: "-3¼" },
  { value: -3, label: "-3" }, { value: -2.75, label: "-2¾" }, { value: -2.5, label: "-2½" }, { value: -2.25, label: "-2¼" },
  { value: -2, label: "-2" }, { value: -1.75, label: "-1¾" }, { value: -1.5, label: "-1½" }, { value: -1.25, label: "-1¼" },
  { value: -1, label: "-1" }, { value: -0.75, label: "-¾" }, { value: -0.5, label: "-½" }, { value: -0.25, label: "-¼" },
  { value: 0, label: "0" },
  { value: 0.25, label: "+¼" }, { value: 0.5, label: "+½" }, { value: 0.75, label: "+¾" }, { value: 1, label: "+1" },
  { value: 1.25, label: "+1¼" }, { value: 1.5, label: "+1½" }, { value: 1.75, label: "+1¾" }, { value: 2, label: "+2" },
  { value: 2.25, label: "+2¼" }, { value: 2.5, label: "+2½" }, { value: 2.75, label: "+2¾" }, { value: 3, label: "+3" },
  { value: 3.25, label: "+3¼" }, { value: 3.5, label: "+3½" }, { value: 3.75, label: "+3¾" }, { value: 4, label: "+4" },
  { value: 4.25, label: "+4¼" }, { value: 4.5, label: "+4½" }, { value: 4.75, label: "+4¾" }, { value: 5, label: "+5" },
  { value: 5.25, label: "+5¼" }, { value: 5.5, label: "+5½" }, { value: 5.75, label: "+5¾" }, { value: 6, label: "+6" },
];

// === COMPREHENSIVE BODY POSTURE OPTIONS (from RCMTM reference charts) ===

// Back Body - Body position forward/backward
const BACK_BODY_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "backward_0.5", label: "Body Backward (0.5cm)" },
  { value: "backward_0.8", label: "Body Backward (0.8cm)" },
  { value: "backward_1.0", label: "Body Backward (1.0cm)" },
  { value: "backward_1.5", label: "Body Backward (1.5cm)" },
  { value: "backward_2.0", label: "Body Backward (2.0cm)" },
  { value: "forward_0.5", label: "Body Forward (0.5cm)" },
  { value: "forward_0.8", label: "Body Forward (0.8cm)" },
  { value: "forward_1.0", label: "Body Forward (1.0cm)" },
  { value: "forward_1.5", label: "Body Forward (1.5cm)" },
  { value: "forward_2.0", label: "Body Forward (2.0cm)" },
];

// Back - Humpback/Stoop/Concave waist
const BACK_POSTURE_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "slight_humpback", label: "Slight Humpback" },
  { value: "moderate_humpback", label: "Moderate Humpback" },
  { value: "very_humpback", label: "Very Humpback" },
  { value: "stoop_1.0", label: "Stoop (1.0cm)" },
  { value: "stoop_2.0", label: "Stoop (2.0cm)" },
  { value: "stoop_3.0", label: "Stoop (3.0cm)" },
  { value: "slight_concave", label: "Slightly Concave Waist" },
  { value: "very_concave", label: "Very Concave Waist" },
];

// Shoulder Blade posture
const SHOULDER_BLADE_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "bulge", label: "Bulge Shoulder Blade" },
  { value: "bulge_top", label: "Bulge Top Shoulder Blade" },
  { value: "inner_higher", label: "Inner Shoulder Blade Higher" },
];

// Neck / Forward Head posture
const NECK_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "slight_forward", label: "Slight Forward Head" },
  { value: "moderate_forward", label: "Moderate Forward Head" },
  { value: "serious_forward", label: "Serious Forward Head" },
];

// Lower Collar posture
const LOWER_COLLAR_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "lower_0.5", label: "Lower Collar 0.5cm" },
  { value: "lower_1.0", label: "Lower Collar 1.0cm" },
  { value: "lower_1.5", label: "Lower Collar 1.5cm" },
  { value: "lower_2.0", label: "Lower Collar 2.0cm" },
  { value: "raise_0.5", label: "Raise Collar 0.5cm" },
  { value: "raise_1.0", label: "Raise Collar 1.0cm" },
  { value: "raise_1.5", label: "Raise Collar 1.5cm" },
  { value: "raise_2.0", label: "Raise Collar 2.0cm" },
];

// Chest posture
const CHEST_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "slight_out", label: "Slight Chest Out" },
  { value: "serious_out", label: "Serious Chest Out" },
  { value: "well_developed", label: "Well-developed Chest" },
];

// Arm posture
const ARM_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "slight_backward", label: "Slight Arms Backward" },
  { value: "very_backward", label: "Very Arms Backward" },
  { value: "slight_forward", label: "Slight Arms Forward" },
  { value: "very_forward", label: "Very Arms Forward" },
  { value: "curved", label: "Curved Arm" },
  { value: "straight", label: "Straight Arm" },
];

// Waist Height
const WAIST_HEIGHT_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "higher_1.0", label: "Higher Waist 1.0cm" },
  { value: "higher_2.0", label: "Higher Waist 2.0cm" },
  { value: "higher_3.0", label: "Higher Waist 3.0cm" },
  { value: "higher_4.0", label: "Higher Waist 4.0cm" },
  { value: "lower_1.0", label: "Lower Waist 1.0cm" },
  { value: "lower_2.0", label: "Lower Waist 2.0cm" },
  { value: "lower_3.0", label: "Lower Waist 3.0cm" },
  { value: "lower_4.0", label: "Lower Waist 4.0cm" },
  { value: "lower_5.0", label: "Lower Waist 5.0cm" },
  { value: "lower_6.0", label: "Lower Waist 6.0cm" },
  { value: "lower_7.0", label: "Lower Waist 7.0cm" },
  { value: "lower_8.0", label: "Lower Waist 8.0cm" },
  { value: "lower_9.0", label: "Lower Waist 9.0cm" },
];

// Shoulder posture (for left and right separately)
const SHOULDER_OPTIONS = [
  { value: "normal", label: "Normal-A" },
  { value: "slight_slope_b", label: "Slight Slope-B" },
  { value: "moderate_slope_c", label: "Moderate Slope-C" },
  { value: "very_slope_d", label: "Very Slope-D" },
  { value: "slight_high_e", label: "Slight High-E" },
  { value: "half_high_f", label: "Half High-F" },
  { value: "high_g", label: "High-G" },
  { value: "square", label: "Square" },
  { value: "rounded", label: "Rounded" },
];

// Cuff measurement options (in inches)
const CUFF_OPTIONS = [
  { value: "8.5", label: '8.5"' },
  { value: "9.0", label: '9"' },
  { value: "9.5", label: '9.5"' },
  { value: "10.0", label: '10"' },
  { value: "10.5", label: '10.5"' },
  { value: "11.0", label: '11"' },
  { value: "11.5", label: '11.5"' },
  { value: "12.0", label: '12"' },
];

// Sleeve length options (in inches)  
const SLEEVE_OPTIONS = [
  { value: "23.0", label: '23"' },
  { value: "23.5", label: '23.5"' },
  { value: "24.0", label: '24"' },
  { value: "24.5", label: '24.5"' },
  { value: "25.0", label: '25"' },
  { value: "25.5", label: '25.5"' },
  { value: "26.0", label: '26"' },
  { value: "26.5", label: '26.5"' },
  { value: "27.0", label: '27"' },
  { value: "27.5", label: '27.5"' },
  { value: "28.0", label: '28"' },
];

// Rise options for trousers (in inches)
const RISE_OPTIONS = [
  { value: "9.0", label: '9"' },
  { value: "9.5", label: '9.5"' },
  { value: "10.0", label: '10"' },
  { value: "10.5", label: '10.5"' },
  { value: "11.0", label: '11"' },
  { value: "11.5", label: '11.5"' },
  { value: "12.0", label: '12"' },
  { value: "12.5", label: '12.5"' },
  { value: "13.0", label: '13"' },
  { value: "13.5", label: '13.5"' },
  { value: "14.0", label: '14"' },
];

// Abdomen posture
const ABDOMEN_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "flat", label: "Flat Stomach" },
  { value: "prominent", label: "Prominent Stomach" },
  { value: "very_prominent", label: "Very Prominent Stomach" },
];

// Seat posture
const SEAT_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "flat", label: "Flat Seat" },
  { value: "prominent", label: "Prominent Seat" },
  { value: "very_prominent", label: "Very Prominent Seat" },
];

// Adjustment selector component - now as table row
const AdjustmentSelect = ({ label, value, onChange, showStarting, startingValue }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-white/5">
    <span className="text-sm text-slate-400">{label}</span>
    <div className="flex items-center gap-2">
      {showStarting && startingValue !== undefined && (
        <span className="text-xs text-slate-500 w-12 text-right">{startingValue}&quot;</span>
      )}
      <Select value={String(value || 0)} onValueChange={(v) => onChange(parseFloat(v))}>
        <SelectTrigger className="w-20 h-7 text-xs bg-[#0a1628] border-white/10">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ADJUSTMENT_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={String(opt.value)} className="text-xs">
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  </div>
);

// Table-based Measurement Row Component (matches reference design) - with editable final
const MeasurementTableRow = ({ label, startingValue, adjustmentValue, onAdjustmentChange, field, allFinals }) => {
  const finalValue = (startingValue || 0) + (adjustmentValue || 0);
  const hasStarting = startingValue !== undefined && startingValue !== null;
  // Only validate if there's an actual final value (skip when starting is empty)
  const warning = hasStarting && field ? validateMeasurement(field, finalValue, allFinals || {}) : null;

  return (
    <tr className="border-b border-white/5">
      <td className="py-1.5 px-2 text-xs text-white font-medium whitespace-nowrap">{label}</td>
      <td className="py-1.5 px-1 text-center text-xs text-slate-400 whitespace-nowrap">
        {hasStarting ? startingValue.toFixed(2) : "–"}
      </td>
      <td className="py-1.5 px-1 text-center">
        <select
          value={adjustmentValue || 0}
          onChange={(e) => onAdjustmentChange(parseFloat(e.target.value) || 0)}
          className="w-20 h-7 text-xs bg-[#0a1628] border border-white/20 rounded text-center text-white appearance-none cursor-pointer"
        >
          {ADJUSTMENT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </td>
      <td className="py-1.5 px-1 text-center">
        <div className="inline-flex items-center gap-1.5">
          <span className="text-xs text-cyan-400 font-mono">{hasStarting ? finalValue.toFixed(2) : "0.00"}</span>
          <MeasurementWarning warning={warning} />
        </div>
      </td>
    </tr>
  );
};

// Table Header Component
const MeasurementTableHeader = () => (
  <thead>
    <tr className="bg-[#1a2a40] text-slate-300">
      <th className="py-1.5 px-2 text-left text-xs font-semibold">Measurement</th>
      <th className="py-1.5 px-1 text-center text-xs font-semibold">Starting</th>
      <th className="py-1.5 px-1 text-center text-xs font-semibold">Adj.</th>
      <th className="py-1.5 px-1 text-center text-xs font-semibold">Final</th>
    </tr>
  </thead>
);

// Posture selector component
const PostureSelect = ({ label, value, onChange, options }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-white/5">
    <span className="text-xs text-slate-400">{label}</span>
    <Select value={value || "normal"} onValueChange={onChange}>
      <SelectTrigger className="w-44 h-7 text-xs bg-[#0a1628] border-white/10">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map(opt => (
          <SelectItem key={opt.value} value={opt.value} className="text-xs">
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const MTMMeasurementsStep = ({
  includeJacket,
  includeTrouser,
  includeVest,
  includeShirt,
  sizeCharts,
  // Jacket
  jacketSize,
  setJacketSize,
  jacketStartingSpecs,
  setJacketStartingSpecs,
  jacketAdjustments,
  setJacketAdjustments,
  jacketPosture,
  setJacketPosture,
  jacketRemarks,
  setJacketRemarks,
  // Trouser
  trouserSize,
  setTrouserSize,
  trouserStartingSpecs,
  setTrouserStartingSpecs,
  trouserAdjustments,
  setTrouserAdjustments,
  trouserPosture,
  setTrouserPosture,
  trouserRemarks,
  setTrouserRemarks,
  // Vest
  vestSize,
  setVestSize,
  vestStartingSpecs,
  setVestStartingSpecs,
  vestAdjustments,
  setVestAdjustments,
  vestRemarks,
  setVestRemarks,
  // Shirt
  shirtSize,
  setShirtSize,
  shirtStartingSpecs,
  setShirtStartingSpecs,
  shirtAdjustments,
  setShirtAdjustments,
  shirtRemarks,
  setShirtRemarks,
}) => {
  // AI Analysis state
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  
  // Handle bulk image upload for AI analysis
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.slice(0, 4).map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setUploadedImages(newImages);
  };

  const removeImage = (index) => {
    setUploadedImages(prev => {
      const newArr = [...prev];
      newArr[index] = undefined;
      return newArr;
    });
  };

  // AI Posture Analysis
  const analyzePosture = async () => {
    const validImages = uploadedImages.filter(Boolean);
    if (validImages.length === 0) {
      toast.error("Please upload at least one photo");
      return;
    }

    setIsAnalyzing(true);
    try {
      // Convert images to base64 (filter out undefined)
      const imagePromises = validImages.map(img => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(img.file);
        });
      });
      
      const base64Images = await Promise.all(imagePromises);

      // Call AI analysis endpoint
      const response = await axios.post(`${API}/ai/analyze-posture`, {
        images: base64Images,
        analysis_type: "body_posture"
      });

      if (response.data.success) {
        setAiAnalysis(response.data.analysis);
        toast.success("Body posture analysis complete!");
      } else {
        toast.error("Analysis failed. Please try again.");
      }
    } catch (error) {
      console.error("AI analysis error:", error);
      toast.error("Failed to analyze photos. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Apply AI analysis results to posture fields
  const applyAIResults = () => {
    if (!aiAnalysis) return;
    
    const newPosture = { ...jacketPosture };
    
    // Map AI results to posture fields
    if (aiAnalysis.back_body) newPosture.back_body = aiAnalysis.back_body;
    if (aiAnalysis.back_posture) newPosture.back_posture = aiAnalysis.back_posture;
    if (aiAnalysis.shoulder_blade) newPosture.shoulder_blade = aiAnalysis.shoulder_blade;
    if (aiAnalysis.neck) newPosture.neck = aiAnalysis.neck;
    if (aiAnalysis.chest) newPosture.chest = aiAnalysis.chest;
    if (aiAnalysis.arm) newPosture.arm = aiAnalysis.arm;
    if (aiAnalysis.shoulder) newPosture.shoulder = aiAnalysis.shoulder;
    if (aiAnalysis.waist_height) newPosture.waist_height = aiAnalysis.waist_height;
    if (aiAnalysis.abdomen) newPosture.abdomen = aiAnalysis.abdomen;
    if (aiAnalysis.seat) newPosture.seat = aiAnalysis.seat;
    if (aiAnalysis.notes) newPosture.notes = (newPosture.notes || "") + "\n\nAI Analysis Notes:\n" + aiAnalysis.notes;
    
    setJacketPosture(newPosture);
    setShowAIDialog(false);
    setUploadedImages([]);
    setAiAnalysis(null);
    toast.success("AI posture analysis applied!");
  };
  
  // Apply jacket size from chart
  const applyJacketSize = (size) => {
    const sizeData = sizeCharts.jacket?.find(s => s.size === size);
    if (sizeData) {
      setJacketStartingSpecs({
        chest: sizeData.chest, 
        stomach: sizeData.stomach, 
        seat: sizeData.seat,
        shoulder: sizeData.shoulder, 
        bicep: sizeData.bicep, 
        sleeve_length: sizeData.sleeve_length,
        back_length: sizeData.back_length, 
        front_length: sizeData.front_length,
        cuff: sizeData.cuff,
        button_stance: sizeData.button_stance
      });
      setJacketSize(size);
      setJacketAdjustments({ 
        chest: 0, stomach: 0, seat: 0, shoulder: 0, bicep: 0, 
        right_sleeve_length: 0, left_sleeve_length: 0, 
        back_length: 0, front_length: 0,
        right_cuff: 0, left_cuff: 0,
        button_stance: 0
      });
    }
  };

  // Apply pant size from chart
  const applyPantSize = (size) => {
    const sizeData = sizeCharts.pant?.find(s => s.size === size);
    if (sizeData) {
      setTrouserStartingSpecs({
        waist: sizeData.waist, 
        seat: sizeData.seat, 
        urise: sizeData.urise,
        thigh: sizeData.thigh, 
        pant_length: sizeData.pant_length, 
        knee: sizeData.knee, 
        bottom: sizeData.bottom
      });
      setTrouserSize(size);
      setTrouserAdjustments({ 
        waist: 0, seat: 0, urise: 0, thigh: 0, 
        right_pant_length: 0, left_pant_length: 0, 
        knee: 0, bottom: 0 
      });
    }
  };

  // Apply vest size from chart
  const applyVestSize = (size) => {
    const sizeData = sizeCharts.vest?.find(s => s.size === size);
    if (sizeData) {
      setVestStartingSpecs({
        chest: sizeData.chest, 
        stomach: sizeData.stomach, 
        back_length: sizeData.back_length,
        front_length: sizeData.front_length,
        bottom: sizeData.bottom
      });
      setVestSize(size);
      setVestAdjustments({ chest: 0, stomach: 0, back_length: 0, front_length: 0, bottom: 0 });
    }
  };

  // Apply shirt size from chart
  const applyShirtSize = (size) => {
    const sizeData = sizeCharts.shirt?.find(s => s.size === size);
    if (sizeData) {
      setShirtStartingSpecs({
        neck: sizeData.neck || 0,
        chest: sizeData.chest || 0,
        stomach: sizeData.stomach || 0,
        seat: sizeData.seat || 0,
        shoulder: sizeData.shoulder || 0,
        sleeve_length: sizeData.sleeve_length || 0,
        back_length: sizeData.back_length || 0,
        cuff: sizeData.cuff || 0,
      });
      setShirtSize(size);
      setShirtAdjustments({
        neck: 0, chest: 0, stomach: 0, seat: 0, shoulder: 0,
        right_sleeve_length: 0, left_sleeve_length: 0,
        back_length: 0, right_cuff: 0, left_cuff: 0,
      });
    }
  };

  // Compute final values per garment for cross-field measurement validation
  const computeFinals = (starting = {}, adj = {}) => {
    const out = {};
    const keys = new Set([...Object.keys(starting || {}), ...Object.keys(adj || {})]);
    keys.forEach((k) => {
      const s = Number(starting?.[k]);
      const a = Number(adj?.[k]);
      out[k] = (Number.isFinite(s) ? s : 0) + (Number.isFinite(a) ? a : 0);
    });
    return out;
  };
  const jacketFinals  = useMemo(() => computeFinals(jacketStartingSpecs,  jacketAdjustments),  [jacketStartingSpecs,  jacketAdjustments]);
  const trouserFinals = useMemo(() => computeFinals(trouserStartingSpecs, trouserAdjustments), [trouserStartingSpecs, trouserAdjustments]);
  const vestFinals    = useMemo(() => computeFinals(vestStartingSpecs,    vestAdjustments),    [vestStartingSpecs,    vestAdjustments]);
  const shirtFinals   = useMemo(() => computeFinals(shirtStartingSpecs,   shirtAdjustments),   [shirtStartingSpecs,   shirtAdjustments]);

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2">Measurements</h2>
        <p className="text-slate-400">Set sizes, adjustments, and body posture for each garment</p>
      </div>

      {/* BODY POSTURE SECTION */}
      <div className="bg-[#111c2e] rounded-xl p-4 border border-amber-500/30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <User size={18} className="text-amber-400" />
            <h3 className="font-bold">Body Posture Assessment</h3>
            <span className="text-xs text-slate-500">(Note customer body characteristics)</span>
          </div>
          {/* AI Analysis Button */}
          <Button
            variant="outline"
            size="sm"
            className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
            onClick={() => setShowAIDialog(true)}
          >
            <Sparkles size={14} className="mr-1" />
            AI Posture Analysis
          </Button>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {/* Row 1: Head & Upper Body */}
          <div className="space-y-1">
            <label className="text-xs text-slate-400 block">Neck / Forward Head</label>
            <Select 
              value={jacketPosture?.neck || "normal"} 
              onValueChange={(v) => setJacketPosture({...jacketPosture, neck: v})}
            >
              <SelectTrigger className="bg-[#0a1628] border-white/10 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NECK_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-slate-400 block">Lower Collar</label>
            <Select 
              value={jacketPosture?.lower_collar || "normal"} 
              onValueChange={(v) => setJacketPosture({...jacketPosture, lower_collar: v})}
            >
              <SelectTrigger className="bg-[#0a1628] border-white/10 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOWER_COLLAR_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-slate-400 block">Left Shoulder Posture</label>
            <Select 
              value={jacketPosture?.left_shoulder || "normal"} 
              onValueChange={(v) => setJacketPosture({...jacketPosture, left_shoulder: v})}
            >
              <SelectTrigger className="bg-[#0a1628] border-white/10 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SHOULDER_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-slate-400 block">Right Shoulder Posture</label>
            <Select 
              value={jacketPosture?.right_shoulder || "normal"} 
              onValueChange={(v) => setJacketPosture({...jacketPosture, right_shoulder: v})}
            >
              <SelectTrigger className="bg-[#0a1628] border-white/10 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SHOULDER_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-slate-400 block">Shoulder Blade</label>
            <Select 
              value={jacketPosture?.shoulder_blade || "normal"} 
              onValueChange={(v) => setJacketPosture({...jacketPosture, shoulder_blade: v})}
            >
              <SelectTrigger className="bg-[#0a1628] border-white/10 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SHOULDER_BLADE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-slate-400 block">Chest</label>
            <Select 
              value={jacketPosture?.chest || "normal"} 
              onValueChange={(v) => setJacketPosture({...jacketPosture, chest: v})}
            >
              <SelectTrigger className="bg-[#0a1628] border-white/10 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHEST_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-slate-400 block">Arm Position</label>
            <Select 
              value={jacketPosture?.arm || "normal"} 
              onValueChange={(v) => setJacketPosture({...jacketPosture, arm: v})}
            >
              <SelectTrigger className="bg-[#0a1628] border-white/10 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ARM_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Row 2: Back & Lower Body */}
          <div className="space-y-1">
            <label className="text-xs text-slate-400 block">Back Body Position</label>
            <Select 
              value={jacketPosture?.back_body || "normal"} 
              onValueChange={(v) => setJacketPosture({...jacketPosture, back_body: v})}
            >
              <SelectTrigger className="bg-[#0a1628] border-white/10 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BACK_BODY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-slate-400 block">Back / Humpback</label>
            <Select 
              value={jacketPosture?.back_posture || "normal"} 
              onValueChange={(v) => setJacketPosture({...jacketPosture, back_posture: v})}
            >
              <SelectTrigger className="bg-[#0a1628] border-white/10 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BACK_POSTURE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-slate-400 block">Waist Height</label>
            <Select 
              value={jacketPosture?.waist_height || "normal"} 
              onValueChange={(v) => setJacketPosture({...jacketPosture, waist_height: v})}
            >
              <SelectTrigger className="bg-[#0a1628] border-white/10 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WAIST_HEIGHT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-slate-400 block">Abdomen / Stomach</label>
            <Select 
              value={jacketPosture?.abdomen || "normal"} 
              onValueChange={(v) => setJacketPosture({...jacketPosture, abdomen: v})}
            >
              <SelectTrigger className="bg-[#0a1628] border-white/10 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ABDOMEN_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-slate-400 block">Seat</label>
            <Select 
              value={jacketPosture?.seat || "normal"} 
              onValueChange={(v) => setJacketPosture({...jacketPosture, seat: v})}
            >
              <SelectTrigger className="bg-[#0a1628] border-white/10 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEAT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Posture Notes */}
        <div className="mt-4">
          <label className="text-xs text-slate-400 mb-1 block">Additional Posture Notes</label>
          <Textarea 
            value={jacketPosture?.notes || ""} 
            onChange={(e) => setJacketPosture({...jacketPosture, notes: e.target.value})}
            placeholder="Note any other body characteristics that affect fit..."
            className="bg-[#0a1628] border-white/10 h-16 text-sm resize-none"
          />
        </div>
      </div>

      {/* GARMENT MEASUREMENTS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Jacket Measurements */}
        {includeJacket && (
          <div className="bg-[#111c2e] rounded-lg p-3 border border-white/10">
            <h3 className="font-bold mb-3 flex items-center gap-2 text-amber-400 text-sm">
              <Shirt size={16} /> Jacket
            </h3>
            
            {/* Model Size */}
            <div className="mb-3">
              <label className="text-xs text-slate-400 mb-1 block">Model Size</label>
              <Select value={jacketSize} onValueChange={applyJacketSize}>
                <SelectTrigger className="bg-[#0a1628] border-white/10 w-32 h-8 text-xs">
                  <SelectValue placeholder="Select Size" />
                </SelectTrigger>
                <SelectContent>
                  {sizeCharts.jacket?.map(s => (
                    <SelectItem key={s.size} value={s.size}>{s.size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Measurements Table */}
            <div className="overflow-x-auto rounded-lg border border-white/10">
              <table className="w-full">
                <MeasurementTableHeader />
                <tbody>
                  <MeasurementTableRow 
                    label="Chest"
                    field="chest"
                    allFinals={jacketFinals}
                    startingValue={jacketStartingSpecs.chest}
                    adjustmentValue={jacketAdjustments.chest}
                    onAdjustmentChange={(v) => setJacketAdjustments({...jacketAdjustments, chest: v})}
                  />
                  <MeasurementTableRow 
                    label="Stomach"
                    field="stomach"
                    allFinals={jacketFinals}
                    startingValue={jacketStartingSpecs.stomach}
                    adjustmentValue={jacketAdjustments.stomach}
                    onAdjustmentChange={(v) => setJacketAdjustments({...jacketAdjustments, stomach: v})}
                  />
                  <MeasurementTableRow 
                    label="Seat"
                    field="seat"
                    allFinals={jacketFinals}
                    startingValue={jacketStartingSpecs.seat}
                    adjustmentValue={jacketAdjustments.seat}
                    onAdjustmentChange={(v) => setJacketAdjustments({...jacketAdjustments, seat: v})}
                  />
                  <MeasurementTableRow 
                    label="Shoulder"
                    field="shoulder"
                    allFinals={jacketFinals}
                    startingValue={jacketStartingSpecs.shoulder}
                    adjustmentValue={jacketAdjustments.shoulder}
                    onAdjustmentChange={(v) => setJacketAdjustments({...jacketAdjustments, shoulder: v})}
                  />
                  <MeasurementTableRow 
                    label="Bicep"
                    startingValue={jacketStartingSpecs.bicep}
                    adjustmentValue={jacketAdjustments.bicep}
                    onAdjustmentChange={(v) => setJacketAdjustments({...jacketAdjustments, bicep: v})}
                  />
                  <MeasurementTableRow 
                    label="Right Sleeve Length"
                    field="sleeve"
                    allFinals={{ sleeve: (jacketStartingSpecs.sleeve_length || 0) + (jacketAdjustments.right_sleeve_length || 0) }}
                    startingValue={jacketStartingSpecs.sleeve_length}
                    adjustmentValue={jacketAdjustments.right_sleeve_length}
                    onAdjustmentChange={(v) => setJacketAdjustments({...jacketAdjustments, right_sleeve_length: v})}
                  />
                  <MeasurementTableRow 
                    label="Left Sleeve Length"
                    field="sleeve"
                    allFinals={{ sleeve: (jacketStartingSpecs.sleeve_length || 0) + (jacketAdjustments.left_sleeve_length || 0) }}
                    startingValue={jacketStartingSpecs.sleeve_length}
                    adjustmentValue={jacketAdjustments.left_sleeve_length}
                    onAdjustmentChange={(v) => setJacketAdjustments({...jacketAdjustments, left_sleeve_length: v})}
                  />
                  <MeasurementTableRow 
                    label="Back Length"
                    field="jacket_length"
                    allFinals={jacketFinals}
                    startingValue={jacketStartingSpecs.back_length}
                    adjustmentValue={jacketAdjustments.back_length}
                    onAdjustmentChange={(v) => setJacketAdjustments({...jacketAdjustments, back_length: v})}
                  />
                  <MeasurementTableRow 
                    label="Front Length"
                    startingValue={jacketStartingSpecs.front_length}
                    adjustmentValue={jacketAdjustments.front_length}
                    onAdjustmentChange={(v) => setJacketAdjustments({...jacketAdjustments, front_length: v})}
                  />
                  <MeasurementTableRow 
                    label="Right Cuff"
                    startingValue={jacketStartingSpecs.cuff}
                    adjustmentValue={jacketAdjustments.right_cuff}
                    onAdjustmentChange={(v) => setJacketAdjustments({...jacketAdjustments, right_cuff: v})}
                  />
                  <MeasurementTableRow 
                    label="Left Cuff"
                    startingValue={jacketStartingSpecs.cuff}
                    adjustmentValue={jacketAdjustments.left_cuff}
                    onAdjustmentChange={(v) => setJacketAdjustments({...jacketAdjustments, left_cuff: v})}
                  />
                  <MeasurementTableRow 
                    label="Button Stance"
                    startingValue={jacketStartingSpecs.button_stance}
                    adjustmentValue={jacketAdjustments.button_stance}
                    onAdjustmentChange={(v) => setJacketAdjustments({...jacketAdjustments, button_stance: v})}
                  />
                </tbody>
              </table>
            </div>

            {/* Remarks */}
            <div className="mt-3">
              <label className="text-xs text-slate-400 mb-1 block">Jacket Notes</label>
              <Textarea 
                value={jacketRemarks || ""} 
                onChange={(e) => setJacketRemarks(e.target.value)}
                placeholder="Jacket notes..."
                className="bg-[#0a1628] border-white/10 h-14 text-xs resize-none"
              />
            </div>
          </div>
        )}

        {/* Trouser Measurements */}
        {includeTrouser && (
          <div className="bg-[#111c2e] rounded-lg p-3 border border-white/10">
            <h3 className="font-bold mb-3 flex items-center gap-2 text-blue-400 text-sm">
              <Ruler size={16} /> Trousers
            </h3>
            
            {/* Model Size */}
            <div className="mb-3">
              <label className="text-xs text-slate-400 mb-1 block">Model Size</label>
              <Select value={trouserSize} onValueChange={applyPantSize}>
                <SelectTrigger className="bg-[#0a1628] border-white/10 w-32 h-8 text-xs">
                  <SelectValue placeholder="Select Size" />
                </SelectTrigger>
                <SelectContent>
                  {sizeCharts.pant?.map(s => (
                    <SelectItem key={s.size} value={s.size}>{s.size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Measurements Table */}
            <div className="overflow-x-auto rounded-lg border border-white/10">
              <table className="w-full">
                <MeasurementTableHeader />
                <tbody>
                  <MeasurementTableRow 
                    label="Waist"
                    field="waist"
                    allFinals={trouserFinals}
                    startingValue={trouserStartingSpecs.waist}
                    adjustmentValue={trouserAdjustments.waist}
                    onAdjustmentChange={(v) => setTrouserAdjustments({...trouserAdjustments, waist: v})}
                  />
                  <MeasurementTableRow 
                    label="Seat"
                    field="seat"
                    allFinals={trouserFinals}
                    startingValue={trouserStartingSpecs.seat}
                    adjustmentValue={trouserAdjustments.seat}
                    onAdjustmentChange={(v) => setTrouserAdjustments({...trouserAdjustments, seat: v})}
                  />
                  <MeasurementTableRow 
                    label="Rise"
                    startingValue={trouserStartingSpecs.urise}
                    adjustmentValue={trouserAdjustments.urise}
                    onAdjustmentChange={(v) => setTrouserAdjustments({...trouserAdjustments, urise: v})}
                  />
                  <MeasurementTableRow 
                    label="Thigh"
                    field="thigh"
                    allFinals={trouserFinals}
                    startingValue={trouserStartingSpecs.thigh}
                    adjustmentValue={trouserAdjustments.thigh}
                    onAdjustmentChange={(v) => setTrouserAdjustments({...trouserAdjustments, thigh: v})}
                  />
                  <MeasurementTableRow 
                    label="Right Pant Length"
                    field="outseam"
                    allFinals={{ outseam: (trouserStartingSpecs.pant_length || 0) + (trouserAdjustments.right_pant_length || 0) }}
                    startingValue={trouserStartingSpecs.pant_length}
                    adjustmentValue={trouserAdjustments.right_pant_length}
                    onAdjustmentChange={(v) => setTrouserAdjustments({...trouserAdjustments, right_pant_length: v})}
                  />
                  <MeasurementTableRow 
                    label="Left Pant Length"
                    field="outseam"
                    allFinals={{ outseam: (trouserStartingSpecs.pant_length || 0) + (trouserAdjustments.left_pant_length || 0) }}
                    startingValue={trouserStartingSpecs.pant_length}
                    adjustmentValue={trouserAdjustments.left_pant_length}
                    onAdjustmentChange={(v) => setTrouserAdjustments({...trouserAdjustments, left_pant_length: v})}
                  />
                  <MeasurementTableRow 
                    label="Knee"
                    field="knee"
                    allFinals={trouserFinals}
                    startingValue={trouserStartingSpecs.knee}
                    adjustmentValue={trouserAdjustments.knee}
                    onAdjustmentChange={(v) => setTrouserAdjustments({...trouserAdjustments, knee: v})}
                  />
                  <MeasurementTableRow 
                    label="Bottom"
                    field="bottom"
                    allFinals={trouserFinals}
                    startingValue={trouserStartingSpecs.bottom}
                    adjustmentValue={trouserAdjustments.bottom}
                    onAdjustmentChange={(v) => setTrouserAdjustments({...trouserAdjustments, bottom: v})}
                  />
                </tbody>
              </table>
            </div>

            {/* Posture Adjustments Section */}
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-xs text-slate-500 mb-2 font-semibold uppercase">Posture Adjustments</p>
              <PostureSelect 
                label="Front Rise" 
                value={trouserPosture?.front_rise || "normal"} 
                onChange={(v) => setTrouserPosture({...trouserPosture, front_rise: v})}
                options={[
                  { value: "normal", label: "Normal" },
                  { value: "high_1", label: "High +1cm" },
                  { value: "high_2", label: "High +2cm" },
                  { value: "low_1", label: "Low -1cm" },
                  { value: "low_2", label: "Low -2cm" },
                ]}
              />
              <PostureSelect 
                label="Back Rise" 
                value={trouserPosture?.back_rise || "normal"} 
                onChange={(v) => setTrouserPosture({...trouserPosture, back_rise: v})}
                options={[
                  { value: "normal", label: "Normal" },
                  { value: "high_1", label: "High +1cm" },
                  { value: "high_2", label: "High +2cm" },
                  { value: "low_1", label: "Low -1cm" },
                  { value: "low_2", label: "Low -2cm" },
                ]}
              />
            </div>

            {/* Remarks */}
            <div className="mt-3">
              <label className="text-xs text-slate-400 mb-1 block">Trouser Notes</label>
              <Textarea 
                value={trouserRemarks || ""} 
                onChange={(e) => setTrouserRemarks(e.target.value)}
                placeholder="Trouser notes..."
                className="bg-[#0a1628] border-white/10 h-14 text-xs resize-none"
              />
            </div>
          </div>
        )}

        {/* Vest Measurements */}
        {includeVest && (
          <div className="bg-[#111c2e] rounded-lg p-3 border border-white/10">
            <h3 className="font-bold mb-3 flex items-center gap-2 text-purple-400 text-sm">
              <Shirt size={16} /> Vest
            </h3>
            
            {/* Model Size */}
            <div className="mb-3">
              <label className="text-xs text-slate-400 mb-1 block">Model Size</label>
              <Select value={vestSize} onValueChange={applyVestSize}>
                <SelectTrigger className="bg-[#0a1628] border-white/10 w-32 h-8 text-xs">
                  <SelectValue placeholder="Select Size" />
                </SelectTrigger>
                <SelectContent>
                  {sizeCharts.vest?.map(s => (
                    <SelectItem key={s.size} value={s.size}>{s.size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Measurements Table */}
            <div className="overflow-x-auto rounded-lg border border-white/10">
              <table className="w-full">
                <MeasurementTableHeader />
                <tbody>
                  <MeasurementTableRow 
                    label="Chest"
                    field="chest"
                    allFinals={vestFinals}
                    startingValue={vestStartingSpecs.chest}
                    adjustmentValue={vestAdjustments.chest}
                    onAdjustmentChange={(v) => setVestAdjustments({...vestAdjustments, chest: v})}
                  />
                  <MeasurementTableRow 
                    label="Stomach"
                    field="stomach"
                    allFinals={vestFinals}
                    startingValue={vestStartingSpecs.stomach}
                    adjustmentValue={vestAdjustments.stomach}
                    onAdjustmentChange={(v) => setVestAdjustments({...vestAdjustments, stomach: v})}
                  />
                  <MeasurementTableRow 
                    label="Back Length"
                    startingValue={vestStartingSpecs.back_length}
                    adjustmentValue={vestAdjustments.back_length}
                    onAdjustmentChange={(v) => setVestAdjustments({...vestAdjustments, back_length: v})}
                  />
                  <MeasurementTableRow 
                    label="Front Length"
                    startingValue={vestStartingSpecs.front_length}
                    adjustmentValue={vestAdjustments.front_length}
                    onAdjustmentChange={(v) => setVestAdjustments({...vestAdjustments, front_length: v})}
                  />
                  <MeasurementTableRow 
                    label="Bottom"
                    startingValue={vestStartingSpecs.bottom}
                    adjustmentValue={vestAdjustments.bottom}
                    onAdjustmentChange={(v) => setVestAdjustments({...vestAdjustments, bottom: v})}
                  />
                </tbody>
              </table>
            </div>

            {/* Remarks */}
            <div className="mt-3">
              <label className="text-xs text-slate-400 mb-1 block">Vest Notes</label>
              <Textarea 
                value={vestRemarks || ""} 
                onChange={(e) => setVestRemarks(e.target.value)}
                placeholder="Vest notes..."
                className="bg-[#0a1628] border-white/10 h-14 text-xs resize-none"
              />
            </div>
          </div>
        )}

        {/* Shirt Measurements */}
        {includeShirt && (
          <div className="bg-[#111c2e] rounded-lg p-3 border border-white/10">
            <h3 className="font-bold mb-3 flex items-center gap-2 text-rose-400 text-sm">
              <Shirt size={16} /> Shirt
            </h3>
            
            {/* Model Size */}
            <div className="mb-3">
              <label className="text-xs text-slate-400 mb-1 block">Model Size</label>
              <Select value={shirtSize} onValueChange={applyShirtSize}>
                <SelectTrigger className="bg-[#0a1628] border-white/10 w-32 h-8 text-xs">
                  <SelectValue placeholder="Select Size" />
                </SelectTrigger>
                <SelectContent>
                  {sizeCharts.shirt?.map(s => (
                    <SelectItem key={s.size} value={s.size}>{s.size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Measurements Table */}
            <div className="overflow-x-auto rounded-lg border border-white/10">
              <table className="w-full">
                <MeasurementTableHeader />
                <tbody>
                  <MeasurementTableRow 
                    label="Neck"
                    field="neck"
                    allFinals={shirtFinals}
                    startingValue={shirtStartingSpecs?.neck}
                    adjustmentValue={shirtAdjustments?.neck}
                    onAdjustmentChange={(v) => setShirtAdjustments({...shirtAdjustments, neck: v})}
                  />
                  <MeasurementTableRow 
                    label="Chest"
                    field="chest"
                    allFinals={shirtFinals}
                    startingValue={shirtStartingSpecs?.chest}
                    adjustmentValue={shirtAdjustments?.chest}
                    onAdjustmentChange={(v) => setShirtAdjustments({...shirtAdjustments, chest: v})}
                  />
                  <MeasurementTableRow 
                    label="Stomach"
                    field="stomach"
                    allFinals={shirtFinals}
                    startingValue={shirtStartingSpecs?.stomach}
                    adjustmentValue={shirtAdjustments?.stomach}
                    onAdjustmentChange={(v) => setShirtAdjustments({...shirtAdjustments, stomach: v})}
                  />
                  <MeasurementTableRow 
                    label="Seat"
                    field="seat"
                    allFinals={shirtFinals}
                    startingValue={shirtStartingSpecs?.seat}
                    adjustmentValue={shirtAdjustments?.seat}
                    onAdjustmentChange={(v) => setShirtAdjustments({...shirtAdjustments, seat: v})}
                  />
                  <MeasurementTableRow 
                    label="Shoulder"
                    field="shoulder"
                    allFinals={shirtFinals}
                    startingValue={shirtStartingSpecs?.shoulder}
                    adjustmentValue={shirtAdjustments?.shoulder}
                    onAdjustmentChange={(v) => setShirtAdjustments({...shirtAdjustments, shoulder: v})}
                  />
                  <MeasurementTableRow 
                    label="Right Sleeve Length"
                    startingValue={shirtStartingSpecs?.sleeve_length}
                    adjustmentValue={shirtAdjustments?.right_sleeve_length}
                    onAdjustmentChange={(v) => setShirtAdjustments({...shirtAdjustments, right_sleeve_length: v})}
                  />
                  <MeasurementTableRow 
                    label="Left Sleeve Length"
                    startingValue={shirtStartingSpecs?.sleeve_length}
                    adjustmentValue={shirtAdjustments?.left_sleeve_length}
                    onAdjustmentChange={(v) => setShirtAdjustments({...shirtAdjustments, left_sleeve_length: v})}
                  />
                  <MeasurementTableRow 
                    label="Back Length"
                    startingValue={shirtStartingSpecs?.back_length}
                    adjustmentValue={shirtAdjustments?.back_length}
                    onAdjustmentChange={(v) => setShirtAdjustments({...shirtAdjustments, back_length: v})}
                  />
                  <MeasurementTableRow 
                    label="Right Cuff"
                    startingValue={shirtStartingSpecs?.cuff}
                    adjustmentValue={shirtAdjustments?.right_cuff}
                    onAdjustmentChange={(v) => setShirtAdjustments({...shirtAdjustments, right_cuff: v})}
                  />
                  <MeasurementTableRow 
                    label="Left Cuff"
                    startingValue={shirtStartingSpecs?.cuff}
                    adjustmentValue={shirtAdjustments?.left_cuff}
                    onAdjustmentChange={(v) => setShirtAdjustments({...shirtAdjustments, left_cuff: v})}
                  />
                </tbody>
              </table>
            </div>

            {/* Remarks */}
            <div className="mt-3">
              <label className="text-xs text-slate-400 mb-1 block">Shirt Notes</label>
              <Textarea 
                value={shirtRemarks || ""} 
                onChange={(e) => setShirtRemarks(e.target.value)}
                placeholder="Shirt notes..."
                className="bg-[#0a1628] border-white/10 h-14 text-xs resize-none"
              />
            </div>
          </div>
        )}
      </div>

      {/* AI Posture Analysis Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="bg-[#111c2e] border-white/10 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="text-purple-400" size={20} />
              AI Body Posture Analysis
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Upload 4 photos of the customer (front, left side, right side, and back views) for comprehensive AI-powered posture analysis.
            </p>

            {/* 4 Image Upload Slots */}
            <div className="grid grid-cols-4 gap-3">
              {["Front View", "Left Side", "Right Side", "Back View"].map((label, idx) => (
                <div key={label} className="space-y-1">
                  <label className="text-xs text-slate-400 text-center block">{label}</label>
                  {uploadedImages[idx] ? (
                    <div className="relative group">
                      <img 
                        src={uploadedImages[idx].preview} 
                        alt={label}
                        className="w-full h-32 object-cover rounded-lg border-2 border-purple-500/50"
                      />
                      <button
                        onClick={() => removeImage(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 shadow-lg"
                      >
                        <X size={12} />
                      </button>
                      <div className="absolute bottom-1 left-1 right-1 text-center">
                        <span className="text-xs bg-black/70 px-2 py-0.5 rounded text-green-400">✓ Uploaded</span>
                      </div>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            const file = e.target.files[0];
                            const newImage = {
                              file,
                              preview: URL.createObjectURL(file),
                              name: file.name
                            };
                            setUploadedImages(prev => {
                              const newArr = [...prev];
                              newArr[idx] = newImage;
                              return newArr;
                            });
                          }
                        }}
                        className="hidden"
                      />
                      <div className="h-32 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center hover:border-purple-500/50 transition-colors">
                        <Camera size={24} className="text-slate-500 mb-1" />
                        <span className="text-xs text-slate-500">Click to add</span>
                      </div>
                    </label>
                  )}
                </div>
              ))}
            </div>

            {/* Quick Upload All 4 */}
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t border-white/10"></div>
              <span className="text-xs text-slate-500">or</span>
              <div className="flex-1 border-t border-white/10"></div>
            </div>
            
            <div className="border-2 border-dashed border-white/20 rounded-lg p-4 text-center hover:border-purple-500/50 transition-colors">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                id="posture-images-bulk"
              />
              <label htmlFor="posture-images-bulk" className="cursor-pointer flex items-center justify-center gap-3">
                <Upload className="text-slate-400" size={24} />
                <div className="text-left">
                  <p className="text-slate-400 text-sm font-medium">Upload all 4 photos at once</p>
                  <p className="text-slate-500 text-xs">Select multiple images from your device</p>
                </div>
              </label>
            </div>

            {/* Upload Status */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">
                {uploadedImages.filter(Boolean).length} of 4 photos uploaded
              </span>
              {uploadedImages.filter(Boolean).length > 0 && (
                <button 
                  onClick={() => setUploadedImages([])}
                  className="text-red-400 hover:text-red-300 text-xs"
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Analyze Button */}
            <Button
              onClick={analyzePosture}
              disabled={uploadedImages.filter(Boolean).length === 0 || isAnalyzing}
              className="w-full bg-purple-500 hover:bg-purple-600"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={16} />
                  Analyzing Body Posture...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2" size={16} />
                  Analyze Posture with AI ({uploadedImages.filter(Boolean).length} photo{uploadedImages.filter(Boolean).length !== 1 ? 's' : ''})
                </>
              )}
            </Button>

            {/* AI Analysis Results */}
            {aiAnalysis && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 space-y-3">
                <h4 className="font-medium flex items-center gap-2 text-purple-400">
                  <Sparkles size={16} /> AI Analysis Results
                </h4>
                
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {aiAnalysis.neck && aiAnalysis.neck !== "normal" && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Neck:</span>
                      <span className="text-purple-300">{NECK_OPTIONS.find(o => o.value === aiAnalysis.neck)?.label || aiAnalysis.neck}</span>
                    </div>
                  )}
                  {aiAnalysis.shoulder && aiAnalysis.shoulder !== "normal" && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Shoulder:</span>
                      <span className="text-purple-300">{SHOULDER_OPTIONS.find(o => o.value === aiAnalysis.shoulder)?.label || aiAnalysis.shoulder}</span>
                    </div>
                  )}
                  {aiAnalysis.shoulder_blade && aiAnalysis.shoulder_blade !== "normal" && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Shoulder Blade:</span>
                      <span className="text-purple-300">{SHOULDER_BLADE_OPTIONS.find(o => o.value === aiAnalysis.shoulder_blade)?.label || aiAnalysis.shoulder_blade}</span>
                    </div>
                  )}
                  {aiAnalysis.back_posture && aiAnalysis.back_posture !== "normal" && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Back:</span>
                      <span className="text-purple-300">{BACK_POSTURE_OPTIONS.find(o => o.value === aiAnalysis.back_posture)?.label || aiAnalysis.back_posture}</span>
                    </div>
                  )}
                  {aiAnalysis.chest && aiAnalysis.chest !== "normal" && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Chest:</span>
                      <span className="text-purple-300">{CHEST_OPTIONS.find(o => o.value === aiAnalysis.chest)?.label || aiAnalysis.chest}</span>
                    </div>
                  )}
                  {aiAnalysis.abdomen && aiAnalysis.abdomen !== "normal" && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Abdomen:</span>
                      <span className="text-purple-300">{ABDOMEN_OPTIONS.find(o => o.value === aiAnalysis.abdomen)?.label || aiAnalysis.abdomen}</span>
                    </div>
                  )}
                </div>

                {aiAnalysis.notes && (
                  <p className="text-xs text-slate-400 mt-2 border-t border-white/10 pt-2">
                    {aiAnalysis.notes}
                  </p>
                )}

                <Button onClick={applyAIResults} className="w-full bg-green-500 hover:bg-green-600">
                  <Check size={16} className="mr-2" />
                  Apply AI Results to Posture Fields
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MTMMeasurementsStep;
